import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DOCUMENT_CATALOG,
  DOCUMENT_KINDS,
  USER_DOCUMENT_STATUSES,
  type DocumentKind,
  type PaginatedResponse,
  type UserDocumentAdminRow,
  type UserDocumentStatus,
} from '@driverforce/shared-types';
import { Card } from '@driverforce/shared-ui';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Forbidden } from '../components/Forbidden';
import { DocumentPreviewModal, type PreviewTarget } from '../components/DocumentPreviewModal';
import { toastApiError } from '../lib/apiToast';

const PAGE_SIZE = 25;

type Filters = {
  status: UserDocumentStatus | '';
  kind: DocumentKind | '';
};

const INITIAL_FILTERS: Filters = {
  status: 'pending_review',
  kind: '',
};

type Page = {
  items: UserDocumentAdminRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; page: Page }
  | { kind: 'error' }
  | { kind: 'forbidden' };

const STATUS_CLASS: Record<UserDocumentStatus, string> = {
  unverified: 'bg-gray-100 text-gray-700',
  pending_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const STATUS_LABEL: Record<UserDocumentStatus, string> = {
  unverified: 'Unverified',
  pending_review: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const describeKind = (kind: DocumentKind): string => {
  const entry = DOCUMENT_CATALOG[kind];
  if (!entry) return kind;
  return entry.translationKey ? `${entry.germanName} (${entry.translationKey})` : entry.germanName;
};

const formatCreatedAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

const formatBytes = (n: number): string => {
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatRelativeUpload = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return formatCreatedAt(iso);
};

const userLabel = (r: UserDocumentAdminRow): { name: string; email: string } => {
  const name = [r.user_first_name ?? '', r.user_last_name ?? ''].filter(Boolean).join(' ').trim();
  return {
    name: name || '—',
    email: r.user_email ?? '—',
  };
};

type UserGroup = {
  userId: string;
  name: string;
  email: string;
  phone: string | null;
  docs: UserDocumentAdminRow[];
};

// The API keeps each user's documents contiguous; Map insertion order
// preserves that ordering.
const groupByUser = (items: UserDocumentAdminRow[]): UserGroup[] => {
  const groups = new Map<string, UserGroup>();
  for (const r of items) {
    let g = groups.get(r.user_id);
    if (!g) {
      const { name, email } = userLabel(r);
      g = { userId: r.user_id, name, email, phone: r.user_phone_e164, docs: [] };
      groups.set(r.user_id, g);
    }
    g.docs.push(r);
  }
  return [...groups.values()];
};

const buildQueryString =(filters: Filters, page: number, pageSize: number): string => {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.kind) params.set('kind', filters.kind);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  return params.toString();
};

export const DocumentsPage: React.FC = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [page, setPage] = useState<number>(1);
  // Per-row mutation state lets us light up the right Approve/Reject button
  // without freezing the rest of the queue.
  const [actingId, setActingId] = useState<string | null>(null);
  // Reject reason modal keyed by row id.
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [previewFile, setPreviewFile] = useState<PreviewTarget | null>(null);

  useEffect(() => {
    setPage(1);
  }, [filters.status, filters.kind]);

  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setState({ kind: 'forbidden' });
      return;
    }
    const myRequestId = ++requestIdRef.current;
    setState({ kind: 'loading' });
    api.setToken(token);
    const qs = buildQueryString(filters, page, PAGE_SIZE);
    const res = await api.get<PaginatedResponse<UserDocumentAdminRow>>(`/admin/documents?${qs}`);
    if (myRequestId !== requestIdRef.current) return; // stale
    if (res.success && res.data) {
      setState({ kind: 'ready', page: res.data });
      return;
    }
    if (res.error?.code === 'FORBIDDEN') {
      setState({ kind: 'forbidden' });
      return;
    }
    toastApiError(res, 'Failed to load documents');
    setState({ kind: 'error' });
  }, [isAdmin, token, page, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(
    () => (state.kind === 'ready' ? groupByUser(state.page.items) : []),
    [state],
  );

  const totalPages = useMemo(() => {
    if (state.kind !== 'ready') return 1;
    return Math.max(1, state.page.totalPages);
  }, [state]);

  const handleApprove = useCallback(
    async (id: string) => {
      if (actingId) return;
      setActingId(id);
      api.setToken(token);
      const res = await api.post<{ id: string; status: UserDocumentStatus }>(
        `/admin/documents/${id}/approve`,
        {},
      );
      setActingId(null);
      if (res.success) {
        await load();
      } else {
        toastApiError(
          res,
          res.error?.code === 'CONFLICT'
            ? 'This document is no longer in pending review.'
            : 'Failed to approve document',
        );
      }
    },
    [actingId, token, load],
  );

  const openRejectModal = useCallback((id: string) => {
    setRejectingId(id);
    setRejectReason('');
  }, []);

  const closeRejectModal = useCallback(() => {
    setRejectingId(null);
    setRejectReason('');
  }, []);

  const submitReject = useCallback(async () => {
    if (!rejectingId) return;
    const reason = rejectReason.trim();
    if (reason.length === 0) return;
    setActingId(rejectingId);
    api.setToken(token);
    const res = await api.post<{ id: string; status: UserDocumentStatus }>(
      `/admin/documents/${rejectingId}/reject`,
      { rejection_reason: reason },
    );
    setActingId(null);
    if (res.success) {
      closeRejectModal();
      await load();
    } else {
      toastApiError(
        res,
        res.error?.code === 'CONFLICT'
          ? 'This document is no longer in pending review.'
          : 'Failed to reject document',
      );
    }
  }, [rejectingId, rejectReason, token, load, closeRejectModal]);

  if (state.kind === 'forbidden') {
    return (
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Document verification</h2>
        <Forbidden />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-4">Document verification</h2>

      {/* Filter bar */}
      <Card padding="md" className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="flex flex-col text-xs font-medium text-gray-600">
            Status
            <select
              className="mt-1 rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  status: e.target.value as UserDocumentStatus | '',
                }))
              }
            >
              <option value="">All statuses</option>
              {USER_DOCUMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col text-xs font-medium text-gray-600">
            Document kind
            <select
              className="mt-1 rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
              value={filters.kind}
              onChange={(e) =>
                setFilters((f) => ({ ...f, kind: e.target.value as DocumentKind | '' }))
              }
            >
              <option value="">All kinds</option>
              {DOCUMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {describeKind(k)}
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-col text-xs font-medium text-gray-600 justify-end">
            <button
              type="button"
              onClick={() => setFilters(INITIAL_FILTERS)}
              className="self-start text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Reset filters
            </button>
          </div>
        </div>
      </Card>

      {state.kind === 'error' && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex items-center justify-between"
        >
          <span>Could not load documents.</span>
          <button
            type="button"
            onClick={() => {
              void load();
            }}
            className="underline ml-2"
          >
            Retry
          </button>
        </div>
      )}

      <Card padding="none">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Files</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {state.kind === 'loading' &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} aria-busy="true">
                  <td colSpan={5} className="px-4 py-3">
                    <span className="block h-6 w-full shimmer" />
                  </td>
                </tr>
              ))}

            {state.kind === 'ready' && state.page.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                  No documents match these filters.
                </td>
              </tr>
            )}

            {groups.map((g) => {
              const pendingCount = g.docs.filter((d) => d.status === 'pending_review').length;
              return (
                <Fragment key={g.userId}>
                  <tr className="bg-gray-50">
                    <td colSpan={5} className="px-4 py-2.5">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                        <span className="text-sm font-semibold text-gray-900">{g.name}</span>
                        <span className="text-xs text-gray-500">{g.email}</span>
                        {g.phone && <span className="text-xs text-gray-500">{g.phone}</span>}
                        <span className="ml-auto text-xs text-gray-500">
                          {g.docs.length} {g.docs.length === 1 ? 'document' : 'documents'}
                          {pendingCount > 0 && (
                            <span className="ml-2 inline-flex px-2 py-0.5 font-semibold rounded-full bg-amber-100 text-amber-800">
                              {pendingCount} pending
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {g.docs.map((r) => {
                    const canReview = r.status === 'pending_review';
                    return (
                      <tr key={r.id}>
                        <td className="px-4 py-3 pl-8 align-top">
                          <div className="text-sm text-gray-800">{describeKind(r.kind)}</div>
                          {r.subtype && (
                            <div className="text-xs text-gray-500">Type: {r.subtype}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap text-sm text-gray-500">
                          {formatCreatedAt(r.created_at)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {r.files.length === 0 ? (
                            <span className="text-gray-400 text-sm">No files</span>
                          ) : (
                            (() => {
                              // The API returns files in upload order (oldest first).
                              // Reverse so the most recent re-upload sits at the top
                              // and is unambiguously the one to review.
                              const ordered = [...r.files].reverse();
                              return (
                                <ul className="space-y-1.5">
                                  {ordered.map((f, idx) => {
                                    const isLatest = idx === 0;
                                    const olderCount = ordered.length - 1;
                                    return (
                                      <li
                                        key={f.id}
                                        className={`text-xs flex items-baseline gap-2 ${
                                          isLatest ? '' : 'opacity-70'
                                        }`}
                                      >
                                        <span
                                          className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                                            isLatest
                                              ? 'bg-blue-100 text-blue-800'
                                              : 'bg-gray-100 text-gray-500'
                                          }`}
                                          title={new Date(f.uploaded_at).toLocaleString()}
                                        >
                                          {isLatest
                                            ? olderCount > 0
                                              ? 'Latest'
                                              : 'v1'
                                            : `v${ordered.length - idx}`}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setPreviewFile({
                                              docId: r.id,
                                              fileId: f.id,
                                              name: f.original_filename ?? f.s3_key.split('/').pop() ?? 'file',
                                              contentType: f.content_type,
                                            })
                                          }
                                          className={`hover:underline truncate ${
                                            isLatest ? 'text-blue-700 font-medium' : 'text-gray-600'
                                          }`}
                                        >
                                          {f.original_filename ?? f.s3_key.split('/').pop() ?? f.id}
                                        </button>
                                        <span className="text-gray-400 shrink-0">
                                          {formatRelativeUpload(f.uploaded_at)} ·{' '}
                                          {formatBytes(f.byte_size)}
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              );
                            })()
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_CLASS[r.status]}`}
                          >
                            {STATUS_LABEL[r.status]}
                          </span>
                          {r.status === 'rejected' && r.rejection_reason && (
                            <div className="mt-1 text-xs text-red-700 max-w-xs">
                              {r.rejection_reason}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                          {canReview ? (
                            <div className="inline-flex gap-2">
                              <button
                                type="button"
                                disabled={actingId === r.id}
                                onClick={() => void handleApprove(r.id)}
                                className="px-3 py-1 rounded bg-green-600 text-white text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                              >
                                {actingId === r.id ? '…' : 'Approve'}
                              </button>
                              <button
                                type="button"
                                disabled={actingId === r.id}
                                onClick={() => openRejectModal(r.id)}
                                className="px-3 py-1 rounded bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </Card>

      {/* Pagination */}
      {state.kind === 'ready' && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
          <div>
            {state.page.total === 0
              ? 'No results'
              : `Showing page ${state.page.page} of ${totalPages} · ${state.page.total} total`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectingId && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-modal-title"
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          onClick={closeRejectModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="reject-modal-title" className="text-base font-semibold text-gray-900 mb-2">
              Reject document
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Explain why this document is being rejected. The user will see this reason and can
              re-upload.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              maxLength={500}
              autoFocus
              placeholder="e.g. The image is blurry — please re-upload a sharper scan of page 1."
              className="w-full rounded-md border-gray-300 text-sm focus:border-red-500 focus:ring-red-500"
            />
            <div className="mt-1 text-xs text-gray-400 text-right">
              {rejectReason.length} / 500
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeRejectModal}
                className="px-3 py-1.5 rounded border border-gray-300 bg-white text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={rejectReason.trim().length === 0 || actingId === rejectingId}
                onClick={() => void submitReject()}
                className="px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {actingId === rejectingId ? 'Rejecting…' : 'Confirm reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewFile && (
        <DocumentPreviewModal target={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
};
